import { RefObject, useEffect, useRef, useState } from 'react'

type DragState = { pointerId: number; startX: number; startOffset: number; moved: boolean; lastX: number; lastT: number; velocity: number }

const CLICK_THRESHOLD_PX = 5

/**
 * Adds mouse/touch drag support to an element and reports back the
 * accumulated horizontal drag offset (in pixels, unbounded/uncapped) and
 * whether a drag is currently in progress.
 *
 * Uses the Pointer Events API so mouse, touch and pen input are all handled
 * through the same code path.
 *
 * @param onDragEnd called when a drag ends, with the pointer's velocity (in
 * pixels/ms, positive meaning it was moving right) at the moment of release.
 * Can be used to drive a momentum/inertia effect after release.
 */
export const useDrag = <T extends HTMLElement>(onDragEnd?: (velocity: number) => void): {
  ref: RefObject<T>
  offset: number
  isDragging: boolean
  /** Adds `delta` to the current offset, e.g. to apply momentum after release. */
  nudge: (delta: number) => void
} => {
  const ref = useRef<T>(null)
  const [offset, setOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const offsetRef = useRef(0)
  const drag = useRef<DragState | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const onPointerDown = (event: PointerEvent): void => {
      if (event.pointerType === 'mouse' && event.button !== 0) return
      drag.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startOffset: offsetRef.current,
        moved: false,
        lastX: event.clientX,
        lastT: event.timeStamp,
        velocity: 0,
      }
      // Pointer capture is deliberately not set here yet: capturing
      // immediately would redirect the eventual click to this element
      // instead of the actual target (e.g. a logo link), breaking normal
      // clicks/taps. It's only captured once the pointer has actually
      // moved past the click threshold, i.e. once it's a real drag.
    }

    const onPointerMove = (event: PointerEvent): void => {
      const current = drag.current
      if (!current || current.pointerId !== event.pointerId) return
      const delta = event.clientX - current.startX
      if (!current.moved && Math.abs(delta) > CLICK_THRESHOLD_PX) {
        current.moved = true
        el.setPointerCapture(current.pointerId)
        setIsDragging(true)
      }
      if (!current.moved) return
      const next = current.startOffset + delta
      offsetRef.current = next
      setOffset(next)

      const dt = event.timeStamp - current.lastT
      if (dt > 0) current.velocity = (event.clientX - current.lastX) / dt
      current.lastX = event.clientX
      current.lastT = event.timeStamp
    }

    const suppressClick = (event: MouseEvent): void => {
      event.preventDefault()
      event.stopPropagation()
    }

    const endDrag = (event: PointerEvent): void => {
      const current = drag.current
      if (!current || current.pointerId !== event.pointerId) return
      if (el.hasPointerCapture(event.pointerId)) el.releasePointerCapture(event.pointerId)
      // Prevent the trailing click (e.g. a logo link) from firing when the
      // pointer actually dragged instead of tapped/clicked.
      if (current.moved) el.addEventListener('click', suppressClick, { capture: true, once: true })
      drag.current = null
      setIsDragging(false)
      if (current.moved) onDragEnd?.(current.velocity)
    }

    el.addEventListener('pointerdown', onPointerDown)
    el.addEventListener('pointermove', onPointerMove)
    el.addEventListener('pointerup', endDrag)
    el.addEventListener('pointercancel', endDrag)

    return (): void => {
      el.removeEventListener('pointerdown', onPointerDown)
      el.removeEventListener('pointermove', onPointerMove)
      el.removeEventListener('pointerup', endDrag)
      el.removeEventListener('pointercancel', endDrag)
    }
  }, [])

  const nudge = (delta: number): void => {
    offsetRef.current += delta
    setOffset(offsetRef.current)
  }

  return { ref, offset, isDragging, nudge }
}
